// src/pages/student/StudentVocabulary.tsx
import React, { useState, useEffect } from "react";
import { FaBook, FaTrophy } from "react-icons/fa";
import VocabularyFlashcard from "../vocab/VocabularyFlashcard";
import PageHeader from "../../core/common/header/PageHeader";

interface VocabularyStats {
  totalWordsLearned: number;
  totalWordsMastered: number;
  currentStreak: number;
  currentLevel: string;
  currentDay: number;
  cycleNumber: number;
  isProductionMode: boolean;
}

const StudentVocabulary: React.FC = () => {
  const API = process.env.REACT_APP_API_BASE_URL;

  const [view, setView] = useState<"today" | "learned" | "random">("today");
  const [words, setWords] = useState<any[]>([]);
  const [stats, setStats] = useState<VocabularyStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWords().then(() => loadStats());
  }, [view]);

  const loadStats = async () => {
    try {
      const res = await fetch(`${API}/api/student/vocabulary/stats`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const loadWords = async () => {
    setLoading(true);
    try {
      let endpoint = "";
      switch (view) {
        case "today":
          endpoint = "/api/student/vocabulary/today";
          break;
        case "learned":
          endpoint = "/api/student/vocabulary/learned";
          break;
        case "random":
          endpoint = "/api/student/vocabulary/review/random?count=20";
          break;
      }

      const res = await fetch(`${API}${endpoint}`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setWords(data);
      }
    } catch (error) {
      console.error("Failed to load words:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (wordId: number) => {
    try {
      await fetch(`${API}/api/student/vocabulary/${wordId}/review`, {
        method: "POST",
        credentials: "include",
      });
      loadStats();
    } catch (error) {
      console.error("Failed to record review:", error);
    }
  };

  const handleMasterToggle = async (wordId: number, mastered: boolean) => {
    try {
      await fetch(
        `${API}/api/student/vocabulary/${wordId}/mastered?mastered=${mastered}`,
        {
          method: "PUT",
          credentials: "include",
        }
      );
      loadWords(); // Reload to update UI
      loadStats(); // Update stats
    } catch (error) {
      console.error("Failed to update mastery:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaBook />}
        title="My Vocabulary Learning"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Vocabulary" },
        ]}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl shadow-md border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FaBook className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">
                    {stats.totalWordsLearned}
                  </p>
                  <p className="text-sm text-gray-600">Words Learned</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl shadow-md border border-green-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FaTrophy className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-600">
                    {stats.totalWordsMastered}
                  </p>
                  <p className="text-sm text-gray-600">Words Mastered</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl shadow-md border border-orange-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🔥</span>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-600">
                    {stats.currentStreak}
                  </p>
                  <p className="text-sm text-gray-600">Day Streak</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl shadow-md border border-purple-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📚</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">
                    {stats.currentLevel || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    Day {stats.currentDay || 0}
                    {stats.cycleNumber > 1 && (
                      <span className="ml-2 inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                        Runde {stats.cycleNumber}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Selector */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setView("today")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-md ${
              view === "today"
                ? "bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            📅 Today's Words
          </button>
          <button
            onClick={() => setView("learned")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-md ${
              view === "learned"
                ? "bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            📖 All Learned
          </button>
          <button
            onClick={() => setView("random")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-md ${
              view === "random"
                ? "bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            🔀 Random Review (20)
          </button>
        </div>

        {/* Flashcard Component */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3]"></div>
            <p className="mt-4 text-gray-600">Loading words...</p>
          </div>
        ) : words.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-lg">
            <p className="text-xl text-gray-600 mb-2">No words available</p>
            <p className="text-sm text-gray-500">
              {view === "today" && "Check back tomorrow for new words!"}
              {view === "learned" &&
                "Start learning to see your progress here."}
              {view === "random" && "Learn some words first to review them."}
            </p>
          </div>
        ) : (
          <VocabularyFlashcard
            words={words}
            onReview={handleReview}
            onMasterToggle={handleMasterToggle}
            isProductionMode={stats?.isProductionMode ?? false}
          />
        )}
      </main>
    </div>
  );
};

export default StudentVocabulary;
